// ABOUTME: The arguments a job was launched with, as label/value rows: readable labels, yes/no for
// ABOUTME: flags, compact JSON for lists and objects; server-derived arguments only for superadmins.

import { Box, Typography } from '@ds/coolshop';
import { useIntl } from 'react-intl';

import { JobArgsSchema } from '../../../types';
import { useArgLabel } from '../../SchemaForm/fieldLabels';
import { InfoEntry, InfoList } from './InfoList';

interface IJobArgumentsProps {
    args: Record<string, unknown> | null | undefined;
    /** The type's args schema, for labels and to recognise server-derived arguments. */
    schema?: JobArgsSchema;
    /** Server-derived arguments are internal: shown to superadmins only, for debugging. */
    showDerived: boolean;
}

const isEmpty = (value: unknown) =>
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

/** The rows of the arguments that have a value, in the schema's order first. */
export const argumentKeys = (
    args: Record<string, unknown>,
    schema: JobArgsSchema | undefined,
    showDerived: boolean,
) => {
    const declared = Object.keys(schema?.properties ?? {});
    const keys = [...declared, ...Object.keys(args).filter(key => !declared.includes(key))];
    return keys.filter(
        key =>
            key in args &&
            !isEmpty(args[key]) &&
            (showDerived || !schema?.properties?.[key]?.['x-derived']),
    );
};

export function JobArguments(props: Readonly<IJobArgumentsProps>) {
    const { args, schema, showDerived } = props;
    const intl = useIntl();
    const argLabel = useArgLabel();

    if (!args) return null;
    const keys = argumentKeys(args, schema, showDerived);
    if (!keys.length) return null;

    const renderValue = (value: unknown) => {
        if (typeof value === 'boolean') {
            return value
                ? intl.formatMessage({ id: 'job.args.yes', defaultMessage: 'Yes' })
                : intl.formatMessage({ id: 'job.args.no', defaultMessage: 'No' });
        }
        if (typeof value === 'object') {
            // Lists and objects (filters, column mappings) stay readable in a small scrolling box.
            return (
                <Box maxHeight={120} overflowY="auto">
                    <Typography
                        variant="caption"
                        fontFamily="monospace"
                        whiteSpace="pre-wrap"
                        wordBreak="break-all"
                    >
                        {JSON.stringify(value)}
                    </Typography>
                </Box>
            );
        }
        return String(value);
    };

    const entries: InfoEntry[] = keys.map(key => ({
        key,
        label: argLabel(key, schema?.properties?.[key]),
        value: renderValue(args[key]),
    }));

    return <InfoList entries={entries} />;
}
